/**
 * Block 13 PR-G Phase 4A — Static template loader (5 JSON variants).
 *
 * 5 templates statically imported (NO dynamic import) — vite bundles ~5KB each
 * as JSON modules into main chunk. Tradeoff: small payload (~25KB total для
 * всех 5 languages × regions); guaranteed available на mount (no async lag);
 * Zod validation один раз на module init.
 *
 * **Reverse: dynamic import** rejected — exotic Service Worker behaviour для
 * JSON imports + race condition если template ещё loading при первом render.
 *
 * **Memoization:** template lookup mutates nothing — return-by-reference safe.
 * Phase 7 clearTransientRequisites не trogает templates (только draft state).
 *
 * Reference:
 *   - ADR-0025 §Q1 (5 JSON templates structure)
 *   - `packages/shared/src/contracts/contracts.ts` ContractTemplateSchema
 */

import {
  ContractTemplateSchema,
  type ContractTemplate,
  type ContractTemplateKey,
} from '@pomoshnik/shared';

import kzRu from './kz.ru.json';
import kzKk from './kz.kk.json';
import ruRu from './ru.ru.json';
import uzRu from './uz.ru.json';
import uzUz from './uz.uz.json';

const RAW_TEMPLATES: Record<ContractTemplateKey, unknown> = {
  'kz.ru': kzRu,
  'kz.kk': kzKk,
  'ru.ru': ruRu,
  'uz.ru': uzRu,
  'uz.uz': uzUz,
};

const TEMPLATE_CACHE: Partial<Record<ContractTemplateKey, ContractTemplate>> = {};

/**
 * Load + validate template per key. Validation один раз на key
 * (subsequent calls возвращают cached parsed object). Throws ZodError если
 * JSON malformed — но `templates-validation.test.ts` гарантирует prod templates
 * валидны в build time.
 */
export function loadContractTemplate(key: ContractTemplateKey): ContractTemplate {
  const cached = TEMPLATE_CACHE[key];
  if (cached) return cached;
  const raw = RAW_TEMPLATES[key];
  const parsed = ContractTemplateSchema.parse(raw);
  TEMPLATE_CACHE[key] = parsed;
  return parsed;
}

/** Тестовый утилит — сбрасывает cache между tests. */
export function _resetTemplateCacheForTests(): void {
  for (const key of Object.keys(TEMPLATE_CACHE) as ContractTemplateKey[]) {
    delete TEMPLATE_CACHE[key];
  }
}
